import Alpine from 'alpinejs';
import axios from '../../config/axios.js';
import { isPanelPath } from '../../utils/sessionCleanup.js';

const DEFAULT_THEMES = [
  'Modern & Minimalist',
  'Bold & Colorful',
  'Classic & Elegant',
  'Professional & Corporate',
  'Fun & Playful',
  'Vintage',
  'Custom',
];

/**
 * Engine 01 — Form INKO (biodata + spesifikasi).
 * Didaftarkan seperti surangHub: x-data="surangInko" (tanpa kurung).
 */
function createSurangInko() {
  return {
    booting: true,
    saving: false,
    parseLoading: false,
    inputMode: 'form',
    promptJson: '',
    chatPrompt: '',
    parseResult: null,
    pendingBriefJson: null,
    layoutTemplates: [],
    composeTemplateKey: '',
    composeResult: null,
    useAiBackground: false,
    exportFormat: 'png',
    exportDpi: 300,
    exportResult: null,
    error: '',
    statusMessage: '',
    generatedImages: [],
    themes: DEFAULT_THEMES.slice(),
    inkoId: null,
    projectId: null,
    assets: [],
    briefJson: null,
    validation: null,
    colorText: 'merah, putih',
    productNamesText: '',
    ornamentsText: '',
    logoPosition: 'left',
    photoPosition: 'center',
    photoMeta: { nama: '', jabatan: '' },
    form: {
      design_type: 'usaha',
      /** Free-form size writing; system parses to metres (3000 → 3 mtr). */
      width_input: '3000',
      height_input: '1000',
      title: '',
      subtitle: '',
      description: '',
      event_date: '',
      whatsapp: '',
      instagram: '',
      youtube: '',
      facebook: '',
      address: '',
      theme: 'Modern & Minimalist',
      instruction: '',
    },

    get logos() {
      return (this.assets || []).filter((a) => a.kind === 'logo');
    },
    get photos() {
      return (this.assets || []).filter((a) => a.kind === 'photo');
    },
    get references() {
      return (this.assets || []).filter((a) => a.kind === 'reference');
    },
    get ocrSources() {
      return (this.assets || []).filter((a) => a.kind === 'ocr_source');
    },
    get hasOcrSource() {
      return this.ocrSources.length > 0;
    },

    async init() {
      this.booting = true;
      this.error = '';
      try {
        if (!isPanelPath()) {
          const auth = Alpine.store('auth');
          if (auth && auth.isLoggedIn === false) {
            window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
            return;
          }
          if (auth && typeof auth.ready === 'function') {
            try { await auth.ready(); } catch { /* ignore */ }
          }
        }
        await this.loadMeta();
      } catch (e) {
        this.themes = DEFAULT_THEMES.slice();
      } finally {
        this.booting = false;
      }
    },

    async loadMeta() {
      try {
        const { data } = await axios.get('/api/v1/surang/inko/meta');
        if (data.data?.themes?.length) {
          this.themes = data.data.themes;
          if (!this.form.theme) this.form.theme = this.themes[0];
        }
        if (Array.isArray(data.data?.layout_templates)) {
          this.layoutTemplates = data.data.layout_templates;
          if (!this.composeTemplateKey && this.layoutTemplates.length) {
            this.composeTemplateKey = this.layoutTemplates[0].key;
          }
        }
      } catch {
        this.themes = DEFAULT_THEMES.slice();
      }
    },

    /** Parse free size → mm. Bare number = mm (3000 → 3 mtr). Also: 300cm, 3m, 3mtr. */
    parseSizeToMm(value) {
      if (value === null || value === undefined || value === '') return null;
      if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        return value;
      }
      let raw = String(value).trim().toLowerCase().replace(',', '.').replace(/\s+/g, '');
      const m = raw.match(/^(\d+(?:\.\d+)?)(mm|cm|mtr|meter|metre|m)?$/);
      if (!m) return null;
      const n = Number(m[1]);
      if (!Number.isFinite(n) || n <= 0) return null;
      const unit = m[2] || 'mm';
      if (unit === 'mm') return n;
      if (unit === 'cm') return n * 10;
      return n * 1000; // m / mtr / meter / metre
    },

    widthMm() {
      return this.parseSizeToMm(this.form.width_input);
    },

    heightMm() {
      return this.parseSizeToMm(this.form.height_input);
    },

    meterLabel(mm) {
      if (!mm) return '';
      const m = mm / 1000;
      return `${String(m.toFixed(3)).replace(/\.?0+$/, '')} mtr`;
    },

    orientationLabel() {
      const w = this.widthMm() || 0;
      const h = this.heightMm() || 0;
      if (!w || !h) return '—';
      if (Math.abs(w - h) < 0.5) return 'Square';
      return w >= h ? 'Landscape' : 'Portrait';
    },

    sizeLabel() {
      const w = this.widthMm();
      const h = this.heightMm();
      if (!w || !h) return '';
      const mw = String((w / 1000).toFixed(2)).replace(/\.?0+$/, '');
      const mh = String((h / 1000).toFixed(2)).replace(/\.?0+$/, '');
      return `${mw}x${mh}`;
    },

    listFromText(text) {
      return String(text || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    },

    positionLabel(position) {
      const map = { left: 'Kiri', center: 'Tengah', right: 'Kanan' };
      return map[position] || position || '—';
    },

    assetFileName(a) {
      const meta = a?.meta || {};
      if (meta.original_name) return meta.original_name;
      if (!a?.url) return a?.id || 'File';
      try {
        const path = String(a.url).split('?')[0];
        const name = path.split('/').pop();
        return decodeURIComponent(name || 'File');
      } catch {
        return 'File';
      }
    },

    isPdfAsset(a) {
      const name = String(this.assetFileName(a) || '').toLowerCase();
      const url = String(a?.url || '').toLowerCase();
      return name.endsWith('.pdf') || url.includes('.pdf');
    },

    isImageAsset(a) {
      if (!a?.url) return false;
      if (this.isPdfAsset(a)) return false;
      return true;
    },

    photoLabel(a) {
      const meta = a.meta || {};
      const bits = ['Foto', this.positionLabel(a.position)];
      if (meta.nama) bits.push(meta.nama);
      if (meta.jabatan) bits.push(meta.jabatan);
      return bits.join(' · ');
    },

    formPreview() {
      return {
        project: {
          title: this.form.title,
          subtitle: this.form.subtitle,
          description: this.form.description,
          event_date: this.form.event_date || null,
        },
        contact: {
          whatsapp: this.form.whatsapp,
          instagram: this.form.instagram,
          youtube: this.form.youtube,
          facebook: this.form.facebook,
          address: this.form.address,
        },
        visual: {
          logos: this.logos,
          photos: this.photos,
          product_names: this.listFromText(this.productNamesText),
          ornaments: this.listFromText(this.ornamentsText),
        },
        design: {
          orientation: this.orientationLabel(),
          size: this.sizeLabel(),
          width_mm: this.widthMm(),
          height_mm: this.heightMm(),
          theme: this.form.theme,
          dominant_color: this.listFromText(this.colorText),
          design_type: this.form.design_type,
        },
        instruction: {
          additional: this.form.instruction,
        },
      };
    },

    payload() {
      const body = {
        design_type: this.form.design_type,
        width: String(this.form.width_input || '').trim(),
        height: String(this.form.height_input || '').trim(),
        width_mm: this.widthMm() ?? undefined,
        height_mm: this.heightMm() ?? undefined,
        title: this.form.title,
        subtitle: this.form.subtitle,
        description: this.form.description,
        event_date: this.form.event_date,
        whatsapp: this.form.whatsapp,
        instagram: this.form.instagram,
        youtube: this.form.youtube,
        facebook: this.form.facebook,
        address: this.form.address,
        theme: this.form.theme,
        instruction: this.form.instruction,
        dominant_color: this.listFromText(this.colorText),
        product_names: this.listFromText(this.productNamesText),
        ornaments: this.listFromText(this.ornamentsText),
      };
      if (this.pendingBriefJson) {
        body.brief_json = this.pendingBriefJson;
      }
      return body;
    },

    applyInko(b) {
      if (!b) return;
      this.inkoId = b.id;
      this.projectId = b.project_id;
      this.briefJson = b.brief_json;
      this.assets = b.assets || [];
      this.validation = b.validation_report;
      if (b.width_mm != null) this.form.width_input = String(b.width_mm);
      if (b.height_mm != null) this.form.height_input = String(b.height_mm);
      const names = b.brief_json && b.brief_json.visual && b.brief_json.visual.product_names;
      const orns = b.brief_json && b.brief_json.visual && b.brief_json.visual.ornaments;
      const colors = b.brief_json && b.brief_json.design && b.brief_json.design.dominant_color;
      if (Array.isArray(names)) this.productNamesText = names.join(', ');
      if (Array.isArray(orns)) this.ornamentsText = orns.join(', ');
      if (Array.isArray(colors) && colors.length) this.colorText = colors.join(', ');
      if (b.brief_json && b.brief_json.project) {
        this.form.title = b.brief_json.project.title || this.form.title;
        this.form.subtitle = b.brief_json.project.subtitle || this.form.subtitle;
        this.form.description = b.brief_json.project.description || this.form.description;
        this.form.event_date = b.brief_json.project.event_date || this.form.event_date;
      }
    },

    async ensureDraft() {
      if (this.inkoId) return true;
      return this.saveInko({ silent: true });
    },

    async parseChat() {
      const prompt = this.chatPrompt.trim();
      if (prompt.length < 12) {
        window.toast?.('Prompt terlalu pendek.', 'error');
        return;
      }
      this.parseLoading = true;
      this.error = '';
      try {
        const { data } = await axios.post('/api/v1/surang/inko/chat-parse', { prompt });
        this.parseResult = data.data || null;
        if (data.data?.brief_json) this.pendingBriefJson = data.data.brief_json;
        window.toast?.(data.message || 'Prompt diparse.', 'success');
        this.applyParseResult({ silent: true });
        this.inputMode = 'form';
      } catch (e) {
        this.error = e.response?.data?.message || 'Gagal parse prompt.';
        window.toast?.(this.error, 'error');
      } finally {
        this.parseLoading = false;
      }
    },

    applyParseResult({ silent = false } = {}) {
      const r = this.parseResult;
      if (!r?.form_fields) return;
      const f = r.form_fields;
      if (f.design_type) this.form.design_type = f.design_type;
      if (f.width_input) this.form.width_input = f.width_input;
      if (f.height_input) this.form.height_input = f.height_input;
      if (f.title != null) this.form.title = f.title;
      if (f.subtitle != null) this.form.subtitle = f.subtitle;
      if (f.description != null) this.form.description = f.description;
      if (f.event_date != null) this.form.event_date = f.event_date;
      if (f.whatsapp != null) this.form.whatsapp = f.whatsapp;
      if (f.instagram != null) this.form.instagram = f.instagram;
      if (f.address != null) this.form.address = f.address;
      if (f.theme) this.form.theme = f.theme;
      if (f.instruction != null) this.form.instruction = f.instruction;
      if (f.color_text != null) this.colorText = f.color_text;
      if (f.product_names_text != null) this.productNamesText = f.product_names_text;
      if (f.ornaments_text != null) this.ornamentsText = f.ornaments_text;
      if (!silent) window.toast?.('Hasil parse diterapkan ke form.', 'success');
    },

    async saveInko({ silent = false } = {}) {
      if (!silent) {
        this.saving = true;
        this.error = '';
      }
      try {
        if (this.inkoId) {
          const { data } = await axios.put(`/api/v1/surang/inko/${this.inkoId}`, this.payload());
          this.applyInko(data.data);
          if (!silent) window.toast?.('Data INKO diperbarui.', 'success');
        } else {
          const { data } = await axios.post('/api/v1/surang/inko', this.payload());
          this.applyInko(data.data);
          if (!silent) window.toast?.('Data INKO disimpan.', 'success');
        }
        return true;
      } catch (e) {
        this.error = e.response?.data?.message || 'Gagal menyimpan data INKO.';
        if (!silent) window.toast?.(this.error, 'error');
        return false;
      } finally {
        if (!silent) this.saving = false;
      }
    },

    async runValidate() {
      if (!this.inkoId) return;
      this.saving = true;
      this.error = '';
      try {
        await this.saveInko();
        const { data } = await axios.post(`/api/v1/surang/inko/${this.inkoId}/validate`);
        this.validation = data.data?.report;
        this.applyInko(data.data?.brief);
        window.toast?.('Validasi selesai.', 'success');
      } catch (e) {
        this.error = e.response?.data?.message || 'Validasi gagal.';
        window.toast?.(this.error, 'error');
      } finally {
        this.saving = false;
      }
    },

    async runOcr() {
      this.saving = true;
      this.error = '';
      try {
        const ready = await this.ensureDraft();
        if (!ready || !this.inkoId) {
          throw new Error(this.error || 'Gagal menyiapkan draft untuk OCR.');
        }
        const { data } = await axios.post('/api/v1/surang/ai/ocr', { brief_id: this.inkoId });
        if (data.data?.brief) this.applyInko(data.data.brief);
        window.toast?.('OCR diproses (stub/resolver).', 'success');
      } catch (e) {
        this.error = e.response?.data?.message || e.message || 'OCR gagal.';
        window.toast?.(this.error, 'error');
      } finally {
        this.saving = false;
      }
    },

    async runCompose() {
      this.error = '';
      this.statusMessage = '';
      this.saving = true;
      try {
        const saved = await this.saveInko({ silent: true });
        if (!saved || !this.inkoId) {
          throw new Error(this.error || 'Data INKO belum tersimpan.');
        }
        const { data } = await axios.post(`/api/v1/surang/inko/${this.inkoId}/compose`, {
          template_key: this.composeTemplateKey || undefined,
          use_ai_background: this.useAiBackground,
          attach_project: false,
        });
        this.composeResult = data.data || null;
        if (data.data?.brief) this.applyInko(data.data.brief);
        this.statusMessage = data.message || 'Layout komposisi siap.';
        window.toast?.(this.statusMessage, 'success');
      } catch (e) {
        this.error = e.response?.data?.message || e.message || 'Compose gagal.';
        window.toast?.(this.error, 'error');
      } finally {
        this.saving = false;
      }
    },

    wireframeStyle(slot) {
      if (!slot) return '';
      return `left:${(slot.x_pct || 0) * 100}%;top:${(slot.y_pct || 0) * 100}%;width:${(slot.w_pct || 0) * 100}%;height:${(slot.h_pct || 0) * 100}%;`;
    },

    async runGeneratePrompt() {
      this.error = '';
      this.statusMessage = '';
      this.promptJson = '';
      this.saving = true;
      try {
        const saved = await this.saveInko({ silent: true });
        if (!saved || !this.inkoId) {
          throw new Error(this.error || 'Data INKO belum tersimpan.');
        }
        const { data } = await axios.post(`/api/v1/surang/inko/${this.inkoId}/generate-prompt`);
        this.promptJson = data.data?.copy_json || JSON.stringify(data.data?.prompt_json || {}, null, 2);
        this.statusMessage = data.message || 'Prompt JSON siap disalin.';
        window.toast?.(this.statusMessage, 'success');
      } catch (e) {
        this.error = e.response?.data?.message || e.message || 'Generate gagal.';
        window.toast?.(this.error, 'error');
      } finally {
        this.saving = false;
      }
    },

    copyPromptJson() {
      if (!this.promptJson) return;
      navigator.clipboard?.writeText(this.promptJson)
        .then(() => window.toast?.('JSON prompt disalin.', 'success'))
        .catch(() => window.toast?.('Gagal menyalin.', 'error'));
    },

    async runAutomate() {
      this.error = '';
      this.statusMessage = '';
      this.generatedImages = [];
      this.saving = true;
      try {
        const saved = await this.saveInko({ silent: true });
        if (!saved || !this.inkoId) {
          throw new Error(this.error || 'Data INKO belum tersimpan.');
        }
        this.statusMessage = 'INKO membuat gambar dari data form…';
        const { data } = await axios.post(`/api/v1/surang/inko/${this.inkoId}/automate`, {
          use_cache: false,
        });

        if (data.data?.async) {
          this.statusMessage = data.message || 'Generate antri — menunggu inference GPU…';
          const polled = await this.pollAutomateStatus();
          if (polled) {
            data.data = polled;
          }
        }

        this.applyInko(data.data?.brief);
        this.generatedImages = Array.isArray(data.data?.images) ? data.data.images : [];
        this.statusMessage = data.data?.message || data.message || 'Generate selesai.';
        if (data.data?.status === false && !this.generatedImages.some((i) => i.url)) {
          this.error = this.statusMessage;
          window.toast?.(this.error, 'error');
        } else {
          window.toast?.(this.statusMessage, 'success');
        }
      } catch (e) {
        this.error = e.response?.data?.message || e.message || 'Generate gagal.';
        window.toast?.(this.error, 'error');
      } finally {
        this.saving = false;
      }
    },

    async pollAutomateStatus(maxAttempts = 60, intervalMs = 3000) {
      for (let i = 0; i < maxAttempts; i += 1) {
        await new Promise((r) => setTimeout(r, intervalMs));
        const { data } = await axios.get(`/api/v1/surang/inko/${this.inkoId}/automate-status`);
        const payload = data.data || {};
        const st = payload.automation?.status || payload.status;
        if (st === 'completed' && Array.isArray(payload.images) && payload.images.length) {
          return payload;
        }
        if (st === 'failed') {
          throw new Error(payload.message || 'Generate inference gagal.');
        }
        this.statusMessage = payload.message || `Generate berjalan… (${st || 'processing'})`;
      }
      throw new Error('Timeout menunggu generate inference.');
    },

    async runExport() {
      this.error = '';
      this.statusMessage = '';
      this.exportResult = null;
      this.saving = true;
      try {
        const saved = await this.saveInko({ silent: true });
        if (!saved || !this.inkoId) {
          throw new Error(this.error || 'Data INKO belum tersimpan.');
        }
        this.statusMessage = 'Export production file…';
        const { data } = await axios.post(`/api/v1/surang/inko/${this.inkoId}/export`, {
          format: this.exportFormat || 'png',
          dpi: Number(this.exportDpi) || 300,
          include_bleed: true,
        });
        let payload = data.data || {};
        if (payload.async) {
          payload = await this.pollExportStatus();
        }
        this.exportResult = payload.export || null;
        if (payload.brief) this.applyInko(payload.brief);
        this.statusMessage = payload.message || 'Export siap cetak.';
        window.toast?.(this.statusMessage, 'success');
      } catch (e) {
        this.error = e.response?.data?.message || e.message || 'Export gagal.';
        window.toast?.(this.error, 'error');
      } finally {
        this.saving = false;
      }
    },

    async pollExportStatus(maxAttempts = 60, intervalMs = 3000) {
      for (let i = 0; i < maxAttempts; i += 1) {
        await new Promise((r) => setTimeout(r, intervalMs));
        const { data } = await axios.get(`/api/v1/surang/inko/${this.inkoId}/export-status`);
        const payload = data.data || {};
        const st = payload.export_job?.status;
        if (st === 'completed' && payload.export?.url) {
          return payload;
        }
        if (st === 'failed') {
          throw new Error(payload.message || 'Export gagal.');
        }
        this.statusMessage = payload.message || `Export berjalan… (${st || 'processing'})`;
      }
      throw new Error('Timeout menunggu export.');
    },

    async uploadAsset(ev, kind, position = null) {
      const file = ev.target?.files?.[0];
      if (!file) return;
      this.saving = true;
      this.error = '';
      try {
        // Buat draft otomatis — user tidak perlu klik Simpan dulu.
        const ready = await this.ensureDraft();
        if (!ready || !this.inkoId) {
          throw new Error(this.error || 'Gagal menyiapkan draft untuk upload.');
        }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('kind', kind);
        if (position) fd.append('position', position);
        if (kind === 'photo') {
          if (this.photoMeta.nama) fd.append('nama', this.photoMeta.nama);
          if (this.photoMeta.jabatan) fd.append('jabatan', this.photoMeta.jabatan);
        }
        // Jangan set Content-Type manual — biarkan browser menambah boundary multipart.
        const { data } = await axios.post(`/api/v1/surang/inko/${this.inkoId}/assets`, fd);
        this.applyInko(data.data?.brief);
        window.toast?.('Aset diunggah.', 'success');
      } catch (e) {
        const fieldErr = e.response?.data?.errors
          ? Object.values(e.response.data.errors).flat()?.[0]
          : null;
        this.error = fieldErr || e.response?.data?.message || e.message || 'Upload gagal.';
        window.toast?.(this.error, 'error');
      } finally {
        this.saving = false;
        ev.target.value = '';
      }
    },
  };
}

Alpine.data('surangInko', createSurangInko);
